import {
  useCurrentWorkspace,
  useCustomQuery,
  useDeleteMutation,
  useUpdateMutation,
} from '@gripp/shared-logic';
import {
  Button,
  ConfirmationOverlay,
  LoadingIndicator,
  TextInput as TextInputCropCircle,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

// ── Types ──────────────────────────────────────────────────────────────────────

type Props = {
  addonIds: string[];
  onClose: () => void;
};

type AddOnEditFormContentProps = {
  addonId: string;
  workspaceId: { id: string };
  onClose: () => void;
};

// ── Inline edit form ───────────────────────────────────────────────────────────

const formSchema = yup.object({
  name: yup.string().required(),
});

const DEBOUNCE_DELAY = 1000;

/**
 * Inline form rendered in the selection panel when one add-on is selected.
 *
 * Mirrors GroupEditFormContent in groupsSelectionPanel.tsx:
 *   - Name TextInput with debounced auto-save (no explicit Save button)
 *   - Panel column title updates to the add-on name once data loads
 *   - "Delete Add-on" rectangular outline button triggers a ConfirmationOverlay
 *   - Toast on successful delete
 */
const AddOnEditFormContent = ({
  addonId,
  workspaceId,
  onClose,
}: AddOnEditFormContentProps) => {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  // Stable reference so the data-load effect below doesn't re-fire on every
  // context object identity change.
  const setColumnTitle = slideLayout?.setColumnTitle;

  const { data, isLoading } = useCustomQuery({
    query: FIND_ADDON,
    cacheKey: ['addon', addonId],
    variables: { id: addonId },
  });

  const addon = data?.getAddon;

  const { mutateAsync: updateAddon } = useUpdateMutation({
    modelName: 'addon',
    query: UPDATE_ADDON,
  });

  const { mutateAsync: deleteAddon } = useDeleteMutation({
    modelName: 'addon',
    query: DELETE_ADDON,
  });

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const [addonName, setAddonName] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!addon?.name) return;
    const name = addon.name as string;
    reset({ name });
    setAddonName(name);
    // Update the panel column title to the add-on name, matching the Figma V6
    // design which shows the entity name in the toolbar.
    setColumnTitle?.(name);
  }, [addon, reset, setColumnTitle]);

  // Cancel any pending debounced save when the panel unmounts. Without this,
  // a setTimeout started within the 1s window would fire after unmount and
  // invoke handleSubmit(saveAddon)() against an unmounted form.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const saveAddon = useCallback(
    async (values: { name: string }) => {
      await updateAddon({
        input: { id: addonId, name: values.name, workspace: { id: workspaceId.id } },
      });
    },
    [updateAddon, addonId, workspaceId]
  );

  const debouncedSave = useCallback(
    (_values: { name: string }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        handleSubmit(saveAddon)();
      }, DEBOUNCE_DELAY);
    },
    [handleSubmit, saveAddon]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const hideDeleteOverlay = () => slideLayout?.setColumnOverlay(null);

  const handleDeleteConfirm = async () => {
    await deleteAddon({ input: { id: addonId } });
    hideDeleteOverlay();
    Toast.show({
      type: 'blackToast',
      text1: t('addOn.deleteAddon.toastTitle', { defaultValue: 'Woosh!' }),
      text2: t('addOn.deleteAddon.toastMessage', {
        addonName,
        defaultValue: `${addonName} has been deleted.`,
      }),
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
    onClose();
  };

  const handleDeletePress = () => {
    if (slideLayout) {
      slideLayout.setColumnOverlay(
        <ConfirmationOverlay
          title={t('addOn.deleteAddon', { defaultValue: 'Delete Add-on' })}
          subtitle={t('addOn.deleteAddon.warning', {
            defaultValue: 'This action cannot be undone.',
          })}
          confirmLabel={t('addOn.deleteAddon.delete', { defaultValue: 'Delete' })}
          onConfirm={handleDeleteConfirm}
          cancelLabel={t('addOn.deleteAddon.cancel', { defaultValue: 'Cancel' })}
          onCancel={hideDeleteOverlay}
          testID="addon-delete-confirmation"
        />
      );
    }
  };

  if (isLoading) return null;

  return (
    <View style={inlineStyles.content}>
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInputCropCircle
            label={t('addOn.list.name')}
            placeholder={t('addOn.create.placeholder')}
            value={value}
            onBlur={onBlur}
            width="100%"
            hideBorder
            onChangeText={(text) => {
              onChange(text);
              debouncedSave({ name: text });
            }}
          />
        )}
      />

      <Button
        variant="rectangular"
        size="large"
        state="outline"
        onPress={handleDeletePress}
        style={inlineStyles.deleteButton}
        accessibilityLabel={t('addOn.deleteAddon', { defaultValue: 'Delete Add-on' })}
      >
        {t('addOn.deleteAddon', { defaultValue: 'Delete Add-on' })}
      </Button>
    </View>
  );
};

// ── Panel main ─────────────────────────────────────────────────────────────────

export const AddOnsSelectionPanelMain = ({ addonIds, onClose }: Props) => {
  const currentWorkspace = useCurrentWorkspace();

  // Edit targets a single add-on; only surface the inline form for exactly
  // one selection.
  const addonId = addonIds.length === 1 ? addonIds[0] : undefined;

  if (!addonId) return null;

  return currentWorkspace ? (
    <AddOnEditFormContent
      addonId={addonId}
      workspaceId={currentWorkspace}
      onClose={onClose}
    />
  ) : (
    <View style={styles.loadingContainer}>
      <LoadingIndicator flexContainer />
    </View>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    backgroundColor: tokens.colors.neutral[100],
  },
});

const inlineStyles = StyleSheet.create({
  // Unified content section: padding=16, gap via marginTop on delete button
  content: {
    padding: tokens.spacing.lg,
  },
  // Full-width outline delete button (height:48, radius:2 handled by Button component)
  deleteButton: {
    marginTop: tokens.spacing['2xl'],
    width: '100%',
  },
});

// ── GraphQL ────────────────────────────────────────────────────────────────────

const FIND_ADDON = `
  query FindAddon($id: ID!) {
    getAddon(id: $id) {
      id
      name
    }
  }
`;

const UPDATE_ADDON = `
  mutation UpdateAddon($input: MutateAddonInput!) {
    updateAddon(input: $input) {
      id
      name
      workspace {
        id
        name
      }
    }
  }
`;

const DELETE_ADDON = `
  mutation deleteAddon($input: MutateAddonInput!) {
    deleteAddon(input: $input) {
      id
    }
  }
`;
